import { analytics } from "@heycatch/sdk";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { logWarn } from "@/lib/log";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ent, setEnt] = useState(null);
  const refreshEnt = useCallback(() => http.get("/billing/me").then((r) => { setEnt(r.data); return r.data; })
    .catch((e) => { logWarn("Couldn't load plan details", e); return null; }), []);
  useEffect(() => { if (user) refreshEnt(); else setEnt(null); }, [user, refreshEnt]);
  // Reload plan details when the access window, lock or any pass changes state, so chat unlocks without a refresh.
  useEffect(() => {
    if (!ent || ent.is_admin) return undefined;
    const a = ent.access || {};
    const times = [a.window_end, a.available_at, ...(ent.passes || []).filter((p) => p.ends_at).map((p) => p.ends_at)]
      .filter(Boolean).map((t) => new Date(t).getTime() - Date.now() + 1000).filter((ms) => ms > 0 && ms < 2147483000);
    if (!times.length) return undefined;
    const t = setTimeout(refreshEnt, Math.max(1000, Math.min(...times)));
    return () => clearTimeout(t);
  }, [ent, refreshEnt]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
    } catch {
      try {
        await http.post("/auth/refresh");
        const { data } = await http.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    // Google sign-in callbacks are handled by AuthCallback, except a staff identity check, which needs the current session.
    if (window.location.hash?.includes("session_id=") && window.location.pathname !== "/staff/verify") return;
    checkAuth();
  }, [checkAuth]);

  // A suspended account is signed out on the spot (the server has already ended its sessions).
  useEffect(() => {
    const onSuspended = (e) => {
      toast.error(e.detail?.message || "This account has been suspended.", { id: "account-suspended" });
      setUser(false);
    };
    window.addEventListener("account-suspended", onSuspended);
    return () => window.removeEventListener("account-suspended", onSuspended);
  }, []);

  useEffect(() => {
    if (user?.user_id) analytics.setIdentity(user.user_id, { email: user.email, name: user.name, plan: ent?.tier || "free" });
  }, [user?.user_id, user?.email, user?.name, ent?.tier]);
  const logout = useCallback(async () => {
    await http.post("/auth/logout").catch((e) => logWarn("Logout request failed", e));
    analytics.resetIdentity();
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, setUser, logout, checkAuth, ent, refreshEnt }), [user, logout, checkAuth, ent, refreshEnt]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
